import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, RefreshCw, AlertTriangle, TrendingUp, Filter, Flame, Target, Clock, ChevronRight, Loader2, LayoutGrid, GitBranch, BarChart3, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { FrictionPointDetailDialog } from '@/components/admin/FrictionPointDetailDialog';
import { FrictionHeatMap } from '@/components/admin/FrictionHeatMap';
import { FrictionFlowDiagram } from '@/components/admin/FrictionFlowDiagram';
import { FrictionPatternAnalysis } from '@/components/admin/FrictionPatternAnalysis';
import { toast } from 'sonner';
import { generateFrictionSummary, hazardCategoryTranslations, impactTranslations, frictionCauseTranslations, shiftTranslations } from '@/utils/plainLanguageTranslations';

interface FrictionPoint {
  id: string;
  task_name: string | null;
  location: string;
  shift: string | null;
  hazard_category: string;
  primary_cause: string | null;
  report_count: number;
  friction_score: number;
  suggested_fix: string | null;
  estimated_impact: 'high' | 'medium' | 'low' | null;
  site_id?: string | null;
  peak_shift?: string | null;
  peak_hour_start?: number | null;
  peak_hour_end?: number | null;
  affected_teams?: string[] | null;
  affected_worker_count?: number | null;
  cause_distribution?: any;
  example_details?: string[] | null;
}

interface AdminFrictionMappingProps {
  siteId?: string | null;
}

const AdminFrictionMapping = ({ siteId: propSiteId }: AdminFrictionMappingProps = {}) => {
  const [frictionPoints, setFrictionPoints] = useState<FrictionPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>(propSiteId || 'all');
  const [selectedPoint, setSelectedPoint] = useState<FrictionPoint | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [creatingTaskFor, setCreatingTaskFor] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('cards');
  const { toast: toastHook } = useToast();

  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    fetchFrictionPoints();
  }, [selectedSiteFilter]);

  const fetchFrictionPoints = async () => {
    setIsLoading(true);
    try {
      const siteIdToUse = selectedSiteFilter !== 'all' ? selectedSiteFilter : null;
      
      const { data, error } = await supabase.functions.invoke('calculate-friction-points', {
        body: { siteId: siteIdToUse }
      });

      if (error) throw error;
      setFrictionPoints(data?.frictionPoints || []);
    } catch (error) {
      console.error('Error fetching friction points:', error);
      toastHook({
        title: 'Error',
        description: 'Failed to load friction points',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSupervisorTask = async (point: FrictionPoint, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreatingTaskFor(point.id);
    
    try {
      let companyId: string | null = null;
      if (point.site_id) {
        const { data: siteData } = await supabase
          .from('company_locations')
          .select('company_id')
          .eq('id', point.site_id)
          .maybeSingle();
        companyId = siteData?.company_id || null;
      }

      const causeAnalysis = point.cause_distribution;
      let humanDescription = `📍 ${point.location} - ${point.hazard_category.replace(/_/g, ' ')}\n\n`;
      humanDescription += `MENGAPA PERLU PERHATIAN:\n`;
      humanDescription += `${point.report_count} laporan serupa diterima di lokasi ini dalam 90 hari terakhir.\n`;
      humanDescription += `Ini menunjukkan pola masalah yang berulang.\n\n`;
      
      if (causeAnalysis?.distribution?.length > 0) {
        humanDescription += `PENYEBAB UTAMA (dari analisis laporan):\n`;
        causeAnalysis.distribution.forEach((c: any) => {
          const causeLabels: Record<string, string> = {
            'equipment': 'Masalah peralatan',
            'time_pressure': 'Tekanan waktu',
            'training': 'Kurang pelatihan',
            'environment': 'Kondisi lingkungan',
            'ppe': 'APD tidak digunakan',
            'procedure': 'Prosedur tidak jelas',
            'fatigue': 'Kelelahan',
            'communication': 'Komunikasi kurang',
            'supervision': 'Pengawasan kurang'
          };
          humanDescription += `• ${c.count} laporan: ${causeLabels[c.category] || c.category}\n`;
        });
        if (causeAnalysis.example_details?.length > 0) {
          humanDescription += `\nContoh alasan dari pekerja:\n`;
          causeAnalysis.example_details.slice(0, 2).forEach((detail: string) => {
            humanDescription += `"${detail.slice(0, 100)}..."\n`;
          });
        }
        humanDescription += `\n`;
      }
      
      humanDescription += `TINDAKAN YANG DISARANKAN:\n`;
      humanDescription += point.suggested_fix || 'Lakukan inspeksi menyeluruh dan dokumentasikan temuan.';
      humanDescription += `\n\nPrioritas: ${point.estimated_impact === 'high' ? 'TINGGI' : point.estimated_impact === 'medium' ? 'SEDANG' : 'RENDAH'} (skor dampak ${point.friction_score.toFixed(1)})`;

      const { error } = await supabase
        .from('supervisor_tasks')
        .insert({
          title: `Inspeksi: ${point.location} - ${point.hazard_category.replace(/_/g, ' ')}`,
          description: humanDescription,
          priority: point.estimated_impact === 'high' ? 'high' : point.estimated_impact === 'medium' ? 'medium' : 'low',
          task_category: 'inspect',
          source_type: 'friction_map',
          source_id: null,
          status: 'pending',
          site_id: point.site_id,
          company_id: companyId,
          source_context: {
            friction_point_id: point.id,
            location: point.location,
            hazard_category: point.hazard_category,
            report_count: point.report_count,
            friction_score: point.friction_score,
            primary_cause: point.primary_cause,
            suggested_fix: point.suggested_fix,
            estimated_impact: point.estimated_impact,
            cause_distribution: causeAnalysis
          }
        });

      if (error) throw error;
      toast.success('Inspection task created successfully');
    } catch (error) {
      console.error('Error creating supervisor task:', error);
      toast.error('Failed to create supervisor task');
    } finally {
      setCreatingTaskFor(null);
    }
  };

  const getImpactConfig = (impact: string | null) => {
    switch (impact) {
      case 'high': 
        return { 
          bg: 'bg-gradient-to-br from-destructive/10 to-destructive/5',
          border: 'border-destructive/30',
          badge: 'bg-destructive text-destructive-foreground',
          icon: 'text-destructive',
          label: impactTranslations['high'] || 'Dampak Tinggi'
        };
      case 'medium': 
        return { 
          bg: 'bg-gradient-to-br from-warning/10 to-warning/5',
          border: 'border-warning/30',
          badge: 'bg-warning text-warning-foreground',
          icon: 'text-warning',
          label: impactTranslations['medium'] || 'Dampak Sedang'
        };
      case 'low': 
        return { 
          bg: 'bg-gradient-to-br from-success/10 to-success/5',
          border: 'border-success/30',
          badge: 'bg-success text-success-foreground',
          icon: 'text-success',
          label: impactTranslations['low'] || 'Dampak Rendah'
        };
      default: 
        return { 
          bg: 'bg-muted/30',
          border: 'border-border',
          badge: 'bg-muted text-muted-foreground',
          icon: 'text-muted-foreground',
          label: 'Tidak Diketahui'
        };
    }
  };

  const categories = [...new Set(frictionPoints.map(fp => fp.hazard_category))];
  const filteredPoints = selectedCategory 
    ? frictionPoints.filter(fp => fp.hazard_category === selectedCategory)
    : frictionPoints;

  const handleCardClick = (point: FrictionPoint) => {
    setSelectedPoint(point);
    setIsDetailOpen(true);
  };

  // Summary stats
  const highImpactCount = frictionPoints.filter(fp => fp.estimated_impact === 'high').length;
  const totalReports = frictionPoints.reduce((sum, fp) => sum + fp.report_count, 0);
  const avgScore = frictionPoints.length > 0 
    ? (frictionPoints.reduce((sum, fp) => sum + fp.friction_score, 0) / frictionPoints.length).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500/10 via-background to-orange-500/10 border border-border/50 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-xl bg-gradient-to-br from-rose-500/20 to-orange-500/20">
                  <MapPin className="h-6 w-6 text-rose-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
                    Peta Titik Masalah
                  </h1>
                  <p className="text-muted-foreground">Lokasi dimana tekanan kerja menyebabkan jalan pintas keselamatan</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!propSiteId && (
                <SiteFilter 
                  value={selectedSiteFilter} 
                  onChange={setSelectedSiteFilter}
                  className="w-[200px]"
                />
              )}
              <Button 
                variant="outline" 
                onClick={fetchFrictionPoints}
                disabled={isLoading}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Summary Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rose-500/10">
                  <Target className="h-5 w-5 text-rose-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{frictionPoints.length}</p>
                  <p className="text-xs text-muted-foreground">Titik Masalah</p>
                </div>
              </div>
            </div>
            <div className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <Flame className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-destructive">{highImpactCount}</p>
                  <p className="text-xs text-muted-foreground">Dampak Tinggi</p>
                </div>
              </div>
            </div>
            <div className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-warning/10">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalReports}</p>
                  <p className="text-xs text-muted-foreground">Total Laporan</p>
                </div>
              </div>
            </div>
            <div className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-success/10">
                  <TrendingUp className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{avgScore}</p>
                  <p className="text-xs text-muted-foreground">Rata-rata Skor</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-12">
          <TabsTrigger value="cards" className="gap-2">
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Kartu</span>
          </TabsTrigger>
          <TabsTrigger value="heatmap" className="gap-2">
            <MapPin className="h-4 w-4" />
            <span className="hidden sm:inline">Peta Panas</span>
          </TabsTrigger>
          <TabsTrigger value="flow" className="gap-2">
            <GitBranch className="h-4 w-4" />
            <span className="hidden sm:inline">Alur</span>
          </TabsTrigger>
          <TabsTrigger value="patterns" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Pola</span>
          </TabsTrigger>
        </TabsList>

        {/* Cards View */}
        <TabsContent value="cards" className="mt-6">
          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap mb-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span>Filter:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge 
                  variant={selectedCategory === null ? "default" : "outline"}
                  className="cursor-pointer hover:bg-primary/80 transition-colors"
                  onClick={() => setSelectedCategory(null)}
                >
                  All ({frictionPoints.length})
                </Badge>
                {categories.map(cat => (
                  <Badge 
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    className="cursor-pointer hover:bg-primary/80 transition-colors"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat.replace(/_/g, ' ')} ({frictionPoints.filter(fp => fp.hazard_category === cat).length})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Friction Points Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <RefreshCw className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
                <p className="text-muted-foreground">Analyzing friction points...</p>
              </div>
            </div>
          ) : filteredPoints.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <div className="p-4 rounded-full bg-muted/50 w-fit mx-auto mb-4">
                  <MapPin className="h-10 w-10 text-muted-foreground/50" />
                </div>
                <h3 className="font-semibold mb-2">Tidak Ada Titik Masalah</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Titik masalah teridentifikasi ketika beberapa laporan bahaya memiliki pola 
                  yang sama di lokasi, tugas, atau kategori.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredPoints.map((point) => {
                const config = getImpactConfig(point.estimated_impact);
                return (
                  <Card 
                    key={point.id} 
                    className={cn(
                      "group cursor-pointer border-2 transition-all duration-300 hover:shadow-lg overflow-hidden",
                      config.bg,
                      config.border,
                      "hover:scale-[1.01]"
                    )}
                    onClick={() => handleCardClick(point)}
                  >
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Impact Indicator Bar */}
                        <div className={cn(
                          "w-2 shrink-0",
                          point.estimated_impact === 'high' ? 'bg-destructive' :
                          point.estimated_impact === 'medium' ? 'bg-warning' : 'bg-success'
                        )} />
                        
                        <div className="flex-1 p-5">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-lg">{point.location}</h3>
                                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {hazardCategoryTranslations[point.hazard_category]?.name || point.hazard_category.replace(/_/g, ' ')}
                              </p>
                            </div>
                            <Badge className={config.badge}>
                              {config.label}
                            </Badge>
                          </div>

                          {/* Plain Language Summary */}
                          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                            <p className="text-sm font-medium mb-2 flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-warning" />
                              Mengapa lokasi ini perlu perhatian?
                            </p>
                            <ul className="text-sm space-y-1">
                              {generateFrictionSummary(point).map((line, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-primary mt-1">•</span>
                                  <span>{line}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Stats Row */}
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="text-center p-2 rounded-lg bg-background/50">
                              <p className="text-lg font-bold">{point.report_count}</p>
                              <p className="text-xs text-muted-foreground">Laporan</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-background/50">
                              <p className="text-lg font-bold">{point.friction_score.toFixed(1)}</p>
                              <p className="text-xs text-muted-foreground">Skor</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-background/50">
                              <p className="text-lg font-bold">{shiftTranslations[point.peak_shift || point.shift || ''] || point.peak_shift || point.shift || '-'}</p>
                              <p className="text-xs text-muted-foreground">Shift Puncak</p>
                            </div>
                          </div>

                          {/* Suggested Fix */}
                          {point.suggested_fix && (
                            <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                              <p className="text-xs text-muted-foreground mb-1">Saran Perbaikan:</p>
                              <p className="text-sm line-clamp-2">{point.suggested_fix}</p>
                            </div>
                          )}

                          {/* Create Task Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-4"
                            onClick={(e) => handleCreateSupervisorTask(point, e)}
                            disabled={creatingTaskFor === point.id}
                          >
                            {creatingTaskFor === point.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Membuat...
                              </>
                            ) : (
                              <>
                                Buat Tugas Supervisor
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Heat Map View */}
        <TabsContent value="heatmap" className="mt-6">
          <FrictionHeatMap 
            points={frictionPoints} 
            onPointClick={handleCardClick}
          />
        </TabsContent>

        {/* Flow Diagram View */}
        <TabsContent value="flow" className="mt-6">
          {frictionPoints.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <div className="p-4 rounded-full bg-muted/50 w-fit mx-auto mb-4">
                  <GitBranch className="h-10 w-10 text-muted-foreground/50" />
                </div>
                <h3 className="font-semibold mb-2">Tidak Ada Titik Masalah</h3>
                <p className="text-sm text-muted-foreground">Pilih titik masalah untuk melihat diagram alurnya</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {frictionPoints.slice(0, 5).map((point) => (
                <Card key={point.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <FrictionFlowDiagram point={point} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pattern Analysis View */}
        <TabsContent value="patterns" className="mt-6">
          <FrictionPatternAnalysis points={frictionPoints} />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <FrictionPointDetailDialog
        point={selectedPoint}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </div>
  );
};

export default AdminFrictionMapping;
